import * as React from "react";
import { Dialog as DialogPrimitive } from "radix-ui";

import { DialogBody } from "./dialog-body";
import { DialogClose } from "./dialog-close";
import { DialogContent } from "./dialog-content";
import { DialogDescription } from "./dialog-description";
import { DialogFooter } from "./dialog-footer";
import { DialogHeader } from "./dialog-header";
import { DialogTitle } from "./dialog-title";

interface DialogProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onExitComplete?: () => void;
}

// Keep cleanup after the shared 180ms exit animation in DialogContent.
export const DIALOG_EXIT_CLEANUP_DELAY_MS = 230;

function Dialog({ children, open, onOpenChange, onExitComplete }: DialogProps) {
  const hasOpenedRef = React.useRef(open === true);
  const onExitCompleteRef = React.useRef(onExitComplete);

  React.useEffect(() => {
    onExitCompleteRef.current = onExitComplete;
  }, [onExitComplete]);

  React.useEffect(() => {
    if (open) {
      hasOpenedRef.current = true;
      return;
    }

    if (open !== false || !hasOpenedRef.current) {
      return;
    }

    const prefersReducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReducedMotion) {
      hasOpenedRef.current = false;
      onExitCompleteRef.current?.();
      return;
    }

    const exitTimer = setTimeout(() => {
      hasOpenedRef.current = false;
      onExitCompleteRef.current?.();
    }, DIALOG_EXIT_CLEANUP_DELAY_MS);

    return () => clearTimeout(exitTimer);
  }, [open]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      {children}
    </DialogPrimitive.Root>
  );
}

Dialog.Body = DialogBody;
Dialog.Close = DialogClose;
Dialog.Content = DialogContent;
Dialog.Description = DialogDescription;
Dialog.Footer = DialogFooter;
Dialog.Header = DialogHeader;
Dialog.Title = DialogTitle;

export { Dialog };
export type { DialogProps };
